import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchUserProducts } from '../../../slice/product/productSlice';
import { fetchCategoryDropdown } from '../../../slice/category/categorySlice';
import Header from '../Header';
import Footer from '../components/Footer';
import { toast } from "react-toastify";
import Popuplogin from "../components/Popuplogin";
import { fetchAllSubCategories } from "../../../slice/subCategory/subCategorySlice";
import {
  toggleFavourite,
  fetchFavourites
} from "../../../slice/favourite/favouriteSlice";

const ITEMS_PER_PAGE = 9;

const Product = () => {
  const { categoryId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [viewImages, setViewImages] = useState([]);
  const [selectedViewImage, setSelectedViewImage] = useState(0);
  const [activeOrientation, setActiveOrientation] = useState('landscape');

  const { userProducts = [], loading, userPagination } = useSelector((state) => state.product);
  const { dropdownCategories = [] } = useSelector((state) => state.category);
  const { user, token } = useSelector((state) => state.auth);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const [filters, setFilters] = useState({
    search: '',
    page: 1,
    limit: ITEMS_PER_PAGE,
  });

  // Reset to page 1 when category changes
  useEffect(() => {
    setFilters(prev => ({ ...prev, page: 1 }));
  }, [categoryId]);

  const defaultImage = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="500"%3E%3Crect fill="%23f0f0f0" width="400" height="500"/%3E%3Ctext x="50%25" y="50%25" font-size="20" fill="%23999" text-anchor="middle" dy=".3em"%3EYour Photo%3C/text%3E%3C/svg%3E';

  const currentCategory = dropdownCategories.find(cat => cat._id === categoryId);

  /* ── Get product display image ── */
  const getProductImage = (product) => {
    if (!product) return defaultImage;
    if (product.previewImage)
      return `${process.env.REACT_APP_API_URL}/uploads/${product.previewImage.replace(/\\/g, '/')}`;
    if (product.images?.length > 0 && product.images[0])
      return `${process.env.REACT_APP_API_URL}/uploads/${product.images[0].replace(/\\/g, '/')}`;
    if (product.colorVariants?.length > 0) {
      const v = product.colorVariants[0];
      if (v.landscape?.images?.length > 0)
        return `${process.env.REACT_APP_API_URL}/uploads/${v.landscape.images[0].replace(/\\/g, '/')}`;
      if (v.portrait?.images?.length > 0)
        return `${process.env.REACT_APP_API_URL}/uploads/${v.portrait.images[0].replace(/\\/g, '/')}`;
      if (v.landscape?.previewImage)
        return `${process.env.REACT_APP_API_URL}/uploads/${v.landscape.previewImage.replace(/\\/g, '/')}`;
    }
    return defaultImage;
  };

  /* ── Fetch dropdown ── */
  useEffect(() => {
    dispatch(fetchCategoryDropdown());
  }, [dispatch]);

  /* ── Fetch products server-side with category + page ── */
  useEffect(() => {
    dispatch(fetchUserProducts({
      page: filters.page,
      limit: filters.limit,
      category: categoryId || '',
    }));
  }, [dispatch, filters.page, filters.limit, categoryId]);

  /* ── Client-side search filter only (category already done server-side) ── */
  const filteredProducts = useMemo(() => {
    if (!filters.search.trim()) return userProducts;
    return userProducts.filter(product =>
      product.name?.toLowerCase().includes(filters.search.toLowerCase())
    );
  }, [userProducts, filters.search]);

  /* ── Pagination ── */
  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenCustomizer = (product) => {
    navigate(`/customize/${product._id}`);
  };

  /* ── Extract images from product for quick view ── */
  const extractProductImages = (product) => {
    if (!product) return [];
    const allImages = [];

    if (product.previewImage) {
      allImages.push({
        id: 'preview',
        url: `${process.env.REACT_APP_API_URL}/uploads/${product.previewImage.replace(/\\/g, '/')}`,
        thumb: `${process.env.REACT_APP_API_URL}/uploads/${product.previewImage.replace(/\\/g, '/')}`,
        orientation: 'landscape',
        isPreview: true,
      });
    }

    if (product.images?.length > 0) {
      product.images.forEach((img, i) => {
        if (img !== product.previewImage) {
          allImages.push({
            id: `product_${i}`,
            url: `${process.env.REACT_APP_API_URL}/uploads/${img.replace(/\\/g, '/')}`,
            thumb: `${process.env.REACT_APP_API_URL}/uploads/${img.replace(/\\/g, '/')}`,
            orientation: 'landscape',
          });
        }
      });
    }

    if (product.colorVariants?.length > 0) {
      product.colorVariants.forEach((variant, vi) => {
        ['landscape', 'portrait'].forEach((ori) => {
          variant[ori]?.images?.forEach((img, ii) => {
            const imageUrl = `${process.env.REACT_APP_API_URL}/uploads/${img.replace(/\\/g, '/')}`;
            if (!allImages.some(d => d.url === imageUrl)) {
              allImages.push({ id: `${vi}_${ori}_${ii}`, url: imageUrl, thumb: imageUrl, orientation: ori, variantIndex: vi });
            }
          });
        });
      });
    }

    return allImages;
  };

  const getImagesByOrientation = (images, orientation) =>
    images.filter(img => img.orientation === orientation);

  const handleOpenViewModal = (product) => {
    if (!product) return;
    const allImages = extractProductImages(product);
    setViewImages(allImages);
    setSelectedViewImage(0);
    setActiveOrientation('landscape');
  };

  const filteredViewImages = viewImages.filter(img => img.orientation === activeOrientation);
  const hasLandscapeImages = viewImages.some(img => img.orientation === 'landscape');
  const hasPortraitImages = viewImages.some(img => img.orientation === 'portrait');

  const handlePrevImage = () => {
    if (filteredViewImages.length > 0)
      setSelectedViewImage(prev => (prev - 1 + filteredViewImages.length) % filteredViewImages.length);
  };
  const handleNextImage = () => {
    if (filteredViewImages.length > 0)
      setSelectedViewImage(prev => (prev + 1) % filteredViewImages.length);
  };

  /* ── Subcategories ── */
  const { allSubCategories = [] } = useSelector((state) => state.subCategory);
  const [openCategory, setOpenCategory] = useState(null);

  useEffect(() => { dispatch(fetchAllSubCategories()); }, [dispatch]);

  const handleSubCategoryClick = (subCategoryId) => navigate(`/product/${subCategoryId}`);

  const groupedData = useMemo(() => {
    return dropdownCategories.map((category) => ({
      id: category._id,
      name: category.name,
      subcategories: allSubCategories
        .filter(sub => sub.category && (sub.category._id === category._id || sub.category === category._id))
        .map(sub => ({ id: sub._id, name: sub.name, status: sub.status, image: sub.image })),
    }));
  }, [dropdownCategories, allSubCategories]);

  const toggleCategory = (id) => setOpenCategory(openCategory === id ? null : id);

  /* ── Favourites ── */
  const { items: favourites, loading: favLoading } = useSelector((state) => state.favourite);
  const isFavourite = (productId) => favourites.some(fav => String(fav.product?._id) === String(productId));

  const handleFavouriteClick = async (productId) => {
    if (!user || !token) { setShowLoginModal(true); return; }
    const alreadyFav = isFavourite(productId);
    try {
      await dispatch(toggleFavourite(productId)).unwrap();
      toast[alreadyFav ? "info" : "success"](alreadyFav ? "Removed from wishlist 💔" : "Added to wishlist ❤️");
    } catch (error) {
      toast.error(error || "Something went wrong");
    }
  };

  useEffect(() => {
    if (token) dispatch(fetchFavourites());
  }, [dispatch, token]);

  const handleShare = async (product) => {
    const shareData = {
      title: product.name,
      text: `Check out ${product.name} - ₹${product.sellingPrice}`,
      url: `${window.location.origin}/product/${product._id}`,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        toast.success("Link copied to clipboard! 🔗");
      }
    } catch (err) {
      if (err.name !== "AbortError") toast.error("Failed to share");
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [categoryId]);

  /* ── Pagination values ── */
  const totalItems = userPagination?.total || 0;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const currentPage = filters.page;
  const startItem = ((currentPage - 1) * ITEMS_PER_PAGE) + 1;
  const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

  return (
    <div className="page-wraper">
      <Header dropdownCategories={dropdownCategories} loading={loading} />
      <div className="page-content">

        {/* Banner */}
        <div className="dz-bnr-inr HeadingHeight">
          <div className="container">
            <div className="dz-bnr-inr-entry HeadCont">
              <h1>{currentCategory ? currentCategory.name : 'All Products'}</h1>
              <nav aria-label="breadcrumb" className="breadcrumb-row">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item"><a href="/">Home</a></li>
                  <li className="breadcrumb-item">{currentCategory ? currentCategory.name : 'Products'}</li>
                </ul>
              </nav>
            </div>
          </div>
        </div>

        <section className="content-inner-3 pt-3 z-index-unset">
          <div className="container-fluid">
            <div className="row mt-xl-2 mt-0">

              {/* ── Sidebar ── */}
              <div className="col-20 col-xl-3 col-md-4">
                <div className="sticky-xl-top">
                  <div className="shop-filter">
                    <aside>
                      <div className="d-flex align-items-center justify-content-between m-b30">
                        <h6 className="title mb-0 fw-normal">
                          <svg className="me-2" width={20} height={20}>
                            <path d="M2.54,5H15v.5A1.5,1.5,0,0,0,16.5,7h2A1.5,1.5,0,0,0,20,5.5V5h2.33a.5.5,0,0,0,0-1H20V3.5A1.5,1.5,0,0,0,18.5,2h-2A1.5,1.5,0,0,0,15,3.5V4H2.54a.5.5,0,0,0,0,1ZM16,3.5a.5.5,0,0,1,.5-.5h2a.5.5,0,0,1,.5.5v2a.5.5,0,0,1-.5.5h-2a.5.5,0,0,1-.5-.5Z" />
                          </svg>
                          Filter
                        </h6>
                      </div>

                      {/* Search */}
                      <div className="widget widget_search">
                        <div className="form-group">
                          <div className="input-group">
                            <input
                              type="search"
                              className="form-control"
                              placeholder="Search Product"
                              value={filters.search}
                              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                            />
                            <div className="input-group-addon">
                              <button className="btn">
                                <i className="iconly-Light-Search"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Categories */}
                      <div className="widget widget_categories">
                        <div className="card mt-3 shadow-sm">
                          <div className="card-header text-center text-white d-flex justify-content-center">
                            <h5 className="mb-0">Categories & Subcategories</h5>
                          </div>
                          <div className="card-body p-0">
                            {loading ? (
                              <p className="text-center p-4">Loading...</p>
                            ) : groupedData.length === 0 ? (
                              <p className="text-center p-4 text-muted">No categories found</p>
                            ) : (
                              groupedData.map((category) => (
                                <div key={category.id} className="border-bottom">
                                  <div
                                    className="p-3 d-flex justify-content-between align-items-center"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => toggleCategory(category.id)}
                                  >
                                    <strong>
                                      {category.name}{' '}
                                      <span className="COINT">{category.subcategories.length}</span>
                                    </strong>
                                  </div>
                                  {openCategory === category.id && (
                                    <div className="ps-4 pb-3">
                                      {category.subcategories.length > 0 ? (
                                        category.subcategories.map((sub) => (
                                          <div
                                            key={sub.id}
                                            className="py-1 d-flex align-items-center gap-3"
                                            style={{ cursor: "pointer" }}
                                            onClick={() => handleSubCategoryClick(sub.id)}
                                          >
                                            {sub.image && (
                                              <img
                                                src={`${process.env.REACT_APP_API_URL}/uploads/subcategories/${sub.image}`}
                                                alt={sub.name}
                                                className="imgSUB"
                                                style={{ objectFit: "cover", borderRadius: "4px" }}
                                              />
                                            )}
                                            <span>{sub.name}</span>
                                          </div>
                                        ))
                                      ) : (
                                        <div
                                          className="BlackColor"
                                          style={{ cursor: "pointer" }}
                                          onClick={() => navigate(`/product/${category.id}`)}
                                        >
                                          No subcategories found
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    </aside>
                  </div>
                </div>
              </div>

              {/* ── Products Grid ── */}
              <div className="col-80 col-xl-9 col-md-8">

                {/* Results count */}
                <div className="RE_filter-wrapper">
                  <div className="RE_filter-left-area">
                    {totalItems > 0 && (
                      <span className="RE_filter-results-text">
                        Showing <strong>{startItem}–{endItem}</strong> of{' '}
                        <strong>{totalItems}</strong> Results
                      </span>
                    )}
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <div className="row gx-xl-4 g-3">
                    {filteredProducts.length > 0 ? (
                      filteredProducts.map((product) => (
                        <div key={product._id} className="col-6 col-xl-4 col-lg-4 col-md-6 col-sm-6 m-md-b15 relative">
                          <div className="SP_container">
                            {product?.label?.name && (
                              <div className="DR_productLabel">{product.label.name}</div>
                            )}
                            <div className="SP_card">
                              <div className="SP_image_wrapper">
                                <img
                                  src={getProductImage(product)}
                                  className="SP_frame_image2"
                                  alt={product.name}
                                  onError={(e) => { e.target.src = defaultImage; }}
                                />
                                <div
                                  className={`btn-primary meta-icon dz-wishicon ${isFavourite(product._id) ? "active" : ""} ${favLoading ? "disabled" : ""}`}
                                  onClick={() => handleFavouriteClick(product._id)}
                                >
                                  <i className="fa-solid fa-heart fill"></i>
                                  <i className="fa-regular fa-heart unfill"></i>
                                </div>
                                <div className="btn btn-primary meta-icon dz-carticon" onClick={() => handleShare(product)}>
                                  <i className="fa-solid fa-share-nodes"></i>
                                </div>
                                <div
                                  className="btn btn-primary meta-icon dz-view"
                                  data-bs-toggle="modal"
                                  data-bs-target="#quick-view"
                                  onClick={() => handleOpenViewModal(product)}
                                >
                                  <i className="fa-solid fa-eye"></i>
                                </div>
                              </div>
                              <div className="rowMAIN">
                                <h3 className="SP_title">{product.name}</h3>
                                <div className="SP_price_row">
                                  <span className="SP_current_price">
                                    ₹{product.sizes?.[0]?.sellingPrice ?? product.sellingPrice}
                                  </span>
                                  <span className="SP_original_price">
                                    ₹{product.sizes?.[0]?.mrp ?? product.mrp}
                                  </span>
                                </div>
                                <button
                                  className="orangeBtn"
                                  onClick={() => handleOpenCustomizer(product)}
                                >
                                  Upload &amp; Customize
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-12 text-center py-5">
                        <h4>No products found</h4>
                        <p>Try adjusting your filters</p>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Pagination ── */}
                {totalPages > 1 && (
                  <div className="row page mt-4">
                    <div className="col-md-6">
                      <p className="page-text">
                        Showing {startItem}–{endItem} of {totalItems} Results
                      </p>
                    </div>
                    <div className="col-md-6">
                      <nav aria-label="Product Pagination">
                        <ul className="pagination style-1">
                          {/* Prev */}
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <a
                              className="page-link prev"
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                if (currentPage > 1) handlePageChange(currentPage - 1);
                              }}
                            >
                              Prev
                            </a>
                          </li>

                          {/* Page numbers */}
                          {[...Array(totalPages)].map((_, i) => {
                            const pageNum = i + 1;
                            // Show first, last, current, and neighbors
                            const showPage =
                              pageNum === 1 ||
                              pageNum === totalPages ||
                              Math.abs(pageNum - currentPage) <= 1;

                            if (!showPage) {
                              // Show ellipsis only once between gaps
                              const prevPage = i;
                              const showEllipsis =
                                prevPage === 0 ||
                                (Math.abs(prevPage - currentPage) === 2 && prevPage !== 0);
                              return showEllipsis ? (
                                <li key={`ellipsis-${i}`} className="page-item disabled">
                                  <span className="page-link">…</span>
                                </li>
                              ) : null;
                            }

                            return (
                              <li
                                key={pageNum}
                                className={`page-item ${currentPage === pageNum ? 'active' : ''}`}
                              >
                                <a
                                  className="page-link"
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange(pageNum);
                                  }}
                                >
                                  {pageNum}
                                </a>
                              </li>
                            );
                          })}

                          {/* Next */}
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <a
                              className="page-link next"
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                if (currentPage < totalPages) handlePageChange(currentPage + 1);
                              }}
                            >
                              Next
                            </a>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ── Quick View Modal ── */}
      <div className="modal quick-view-modal fade" id="quick-view" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden', border: 'none' }}>
            <button
              type="button"
              data-bs-dismiss="modal"
              aria-label="Close"
              style={{
                position: 'absolute', top: '14px', right: '14px',
                width: '34px', height: '34px', borderRadius: '50%',
                border: '1px solid #e5e7eb', background: '#fff',
                cursor: 'pointer', zIndex: 10, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                fontSize: '18px', color: '#6b7280', lineHeight: 1,
              }}
            >
              &#x2715;
            </button>

            <div className="modal-body p-0" style={{ minHeight: '520px' }}>
              <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px', background: '#fafafa' }}>

                {/* Orientation Tabs */}
                {(hasLandscapeImages || hasPortraitImages) && (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {hasLandscapeImages && (
                      <button
                        onClick={() => { setActiveOrientation('landscape'); setSelectedViewImage(0); }}
                        style={{
                          padding: '6px 16px', borderRadius: '8px',
                          background: activeOrientation === 'landscape' ? '#f97316' : '#fff',
                          color: activeOrientation === 'landscape' ? '#fff' : '#6b7280',
                          border: activeOrientation === 'landscape' ? 'none' : '1px solid #e5e7eb',
                          cursor: 'pointer', fontWeight: 600, fontSize: '13px',
                        }}
                      >
                        🖼️ Landscape ({getImagesByOrientation(viewImages, 'landscape').length})
                      </button>
                    )}
                    {hasPortraitImages && (
                      <button
                        onClick={() => { setActiveOrientation('portrait'); setSelectedViewImage(0); }}
                        style={{
                          padding: '6px 16px', borderRadius: '8px',
                          background: activeOrientation === 'portrait' ? '#f97316' : '#fff',
                          color: activeOrientation === 'portrait' ? '#fff' : '#6b7280',
                          border: activeOrientation === 'portrait' ? 'none' : '1px solid #e5e7eb',
                          cursor: 'pointer', fontWeight: 600, fontSize: '13px',
                        }}
                      >
                        📱 Portrait ({getImagesByOrientation(viewImages, 'portrait').length})
                      </button>
                    )}
                  </div>
                )}

                {/* Main Image */}
                <div style={{
                  flex: 1, position: 'relative', background: '#fff',
                  borderRadius: '12px', border: '1px solid #f0f0f0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  minHeight: '400px', overflow: 'hidden',
                }}>
                  {filteredViewImages.length > 1 && (
                    <>
                      <button onClick={handlePrevImage} style={{
                        position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                        width: '36px', height: '36px', borderRadius: '50%',
                        background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none',
                        cursor: 'pointer', fontSize: '20px', zIndex: 5,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>‹</button>
                      <button onClick={handleNextImage} style={{
                        position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                        width: '36px', height: '36px', borderRadius: '50%',
                        background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none',
                        cursor: 'pointer', fontSize: '20px', zIndex: 5,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>›</button>
                    </>
                  )}
                  {filteredViewImages.length > 0 ? (
                    <img
                      src={filteredViewImages[selectedViewImage]?.url}
                      alt="Product"
                      style={{ maxWidth: '100%', maxHeight: '450px', objectFit: 'contain', borderRadius: '8px' }}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', color: '#9ca3af' }}>
                      <div style={{ fontSize: '40px', marginBottom: '8px' }}>🖼️</div>
                      <p style={{ fontSize: '14px' }}>No images available</p>
                    </div>
                  )}
                </div>

                {/* Thumbnails */}
                {filteredViewImages.length > 1 && (
                  <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
                    {filteredViewImages.map((image, index) => (
                      <button
                        key={image.id}
                        onClick={() => setSelectedViewImage(index)}
                        style={{
                          width: '60px', height: '60px', flexShrink: 0,
                          border: selectedViewImage === index ? '2px solid #f97316' : '1.5px solid #e5e7eb',
                          borderRadius: '8px', overflow: 'hidden',
                          cursor: 'pointer', padding: 0, background: '#fff',
                        }}
                      >
                        <img src={image.thumb} alt={`Thumb ${index + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </button>
                    ))}
                  </div>
                )}

                {filteredViewImages.length > 0 && (
                  <div style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center' }}>
                    {selectedViewImage + 1} / {filteredViewImages.length} images
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content POP">
              <Popuplogin onClose={() => setShowLoginModal(false)} />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Product;